import { Box } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '../../../../lib/components';
import { BenefitIcon, BenefitKey } from '../../../../lib/components/compound/dataDisplay';
import { CardWidget } from '../../../../lib/components/compound/surfaces/cardWidget';
import { EditIcon } from '../../../../lib/icons';
import { IconEnum, NextMonthBenefitSet } from '@pxtra-client-lib-legacy/gen';
import { useActions } from '../../../../hooks/useActions';
import { useNavigate } from 'react-router';
import { TooltipComponent } from '../../../../lib/components/atomic/feedback';
import { useCreateBenefitSet } from '../../../actions/createBenefitSet/hooks/useCreateBenefitSet';

export interface BenefitData {
  key: string;
  usedBudget: number;
  label?: string | null;
  icon?: IconEnum;
}

export interface Props {
  benefitSet: NextMonthBenefitSet | undefined;
  companyBenefitSetsExists: boolean | undefined;
  employeeId: string | undefined;
}

const BenefitSetEditButton: FC<{
  companyBenefitSetsExists: boolean | undefined;
  employeeId: string | undefined;
}> = ({ companyBenefitSetsExists, employeeId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedEmployees } = useActions();
  const { newBenefitSet } = useCreateBenefitSet();

  const handleClick = () => {
    if (companyBenefitSetsExists && employeeId) {
      setSelectedEmployees({ [employeeId]: true });
      newBenefitSet.assignmentContext = 'employeeDashboard';
      navigate('/actions/benefit-set/assign_benefit_set/1');
    }
  };

  const editIcon = (
    <Box
      className={`absolute ${!companyBenefitSetsExists ? 'right-0' : 'right-5'}`}
      onClick={handleClick}
    >
      <EditIcon
        className={`h-8 w-8 transition-colors ${
          !companyBenefitSetsExists
            ? 'text-grey-400 cursor-default'
            : 'text-grey-600 cursor-pointer hover:text-primary-main '
        }`}
      />
    </Box>
  );
  return !companyBenefitSetsExists ? (
    <TooltipComponent
      title={t('benefitSet:assignmentPossibleIfBenefitSet')}
      arrow
      placement="bottom"
      className="pt-8 pr-10"
    >
      <span className="absolute right-6">{editIcon}</span>
    </TooltipComponent>
  ) : (
    editIcon
  );
};

export const BenefitSetWidget: FC<Props> = ({
  benefitSet,
  companyBenefitSetsExists,
  employeeId
}) => {
  const { t } = useTranslation();

  return (
    <CardWidget
      title={t('benefitSet:benefitSet_one')}
      titleComponent={benefitSet?.name ?? t('common:NOT_ASSIGNED')}
      subTitle={t('common:AVAILABLE_MONTHLY_BENEFITS')}
      icon={
        <BenefitSetEditButton
          companyBenefitSetsExists={companyBenefitSetsExists}
          employeeId={employeeId}
        />
      }
      sx={{
        paddingLeft: '10px',
        margin: 0,
        maxHeight: !benefitSet?.name ? '56px' : 'auto'
      }}
    >
      {benefitSet && benefitSet.enabled_benefits ? (
        <Box className="pl-3">
          {benefitSet.enabled_benefits.map((benefit) => (
            <Chip
              label={t(`benefits:${benefit}`)}
              key={benefit}
              icon={
                <BenefitIcon
                  benefitKey={benefit as BenefitKey}
                  size={25}
                  color="primary.main"
                  sx={{ backgroundColor: 'None' }}
                />
              }
              className="bg-grey-50 border-none text-sm h-full float-left text-primary-main m-1 p-1 rounded-3xl normal-case hover:bg-grey-50"
            />
          ))}
        </Box>
      ) : (
        <></>
      )}
    </CardWidget>
  );
};
